import React from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { silentRegister } from '../../../redux/legacy/reducers/ducks/legacy/AuthorizationDuck';
import { toggleSignIn } from '../../../redux/legacy/reducers/ducks/legacy/MainDuck';
import validationErrors from '../../../util/validation/ValidationErrors';

const phoneRegExp = /^(\d{3})(\d{3})(\d{4})$/;
const phoneRegExpect = /^\(\d{3}\)\s\d{3}-\d{4}$/;

const schema = yup.object().shape({
  firstname: yup.string().required(validationErrors.firstnameRequired),
  lastname: yup.string().required(validationErrors.lastnameRequired),
  email: yup
    .string()
    .required(validationErrors.emailRequired)
    .email(validationErrors.emailInvalid),
  phone: yup
    .string()
    .trim()
    .required(validationErrors.phoneRequired)
    .test(
      'phone-format',
      validationErrors.phoneInvalid,
      (value) => !value || phoneRegExpect.test(value)
    ),
});

function GuestCheckoutForm({ onSignInClick }) {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.authorization?.isLoading);

  const { handleSubmit, control } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
    },
  });

  const onSubmit = (data) => {
    if (isLoading) return;
    dispatch(
      silentRegister({
        firstname: data.firstname,
        lastname: data.lastname,
        contact_email: data.email,
        phone: data.phone,
      })
    );
  };

  const handleSignInClick = (e) => {
    e.preventDefault();
    if (onSignInClick) {
      onSignInClick();
    } else {
      dispatch(toggleSignIn(true));
    }
  };

  return (
    <Box px={3} py={4}>
      <Box display="flex" gap={1} mb={3}>
        <Button
          variant="contained"
          sx={{ flex: 1, textTransform: 'none', fontWeight: 600 }}
          data-test-id="guest-checkout-tab"
          disableElevation
        >
          Guest Checkout
        </Button>
        <Button
          variant="outlined"
          onClick={handleSignInClick}
          sx={{ flex: 1, textTransform: 'none' }}
          data-test-id="guest-signin-tab"
        >
          Sign In
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Enter your info to place your order. No account or password needed.
      </Typography>

      <form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        data-test-id="guest-checkout-form"
      >
        <Controller
          name="firstname"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="First Name"
              fullWidth
              margin="normal"
              error={!!error}
              helperText={error?.message}
              data-test-id="guest-firstname"
            />
          )}
        />

        <Controller
          name="lastname"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Last Name"
              fullWidth
              margin="normal"
              error={!!error}
              helperText={error?.message}
              data-test-id="guest-lastname"
            />
          )}
        />

        <Controller
          name="email"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              error={!!error}
              helperText={error?.message}
              data-test-id="guest-email"
            />
          )}
        />

        <Controller
          name="phone"
          control={control}
          render={({
            field: { onChange, value, ...field },
            fieldState: { error },
          }) => (
            <TextField
              {...field}
              onChange={(ev) => {
                let digits = ev.target.value.replace(/[^\d]/g, '');
                if (digits.length === 11 && digits.startsWith('1')) {
                  digits = digits.slice(1);
                }
                const formatted = digits
                  .slice(0, 10)
                  .replace(phoneRegExp, '($1) $2-$3');
                onChange(formatted);
              }}
              value={value}
              label="Phone"
              required
              placeholder="(999) 999-9999"
              fullWidth
              margin="normal"
              error={!!error}
              helperText={error?.message}
              data-test-id="guest-phone"
            />
          )}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading}
          sx={{ mt: 2, mb: 1 }}
          data-test-id="guest-checkout-submit"
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Continue to Checkout →'
          )}
        </Button>
      </form>
    </Box>
  );
}

GuestCheckoutForm.propTypes = {
  isDelivery: PropTypes.bool,
  onSignInClick: PropTypes.func,
};

GuestCheckoutForm.defaultProps = {
  isDelivery: false,
  onSignInClick: null,
};

export default GuestCheckoutForm;
